import type { ServerFilters } from "@/hooks/useQueryFilters";
import { TableFilterFeature } from "@/lib/table/filters-utils";
import { RowExitingFeature } from "@/lib/table/row-exiting";
import { RowExpanding } from "@/lib/table/row-expanding";
import { RowFocusFeature } from "@/lib/table/row-focus";
import { RowLoadingFeature } from "@/lib/table/row-loading";
import { ServerSideFilteringFeature } from "@/lib/table/server-side-filtering";

import {
  type ColumnDef,
  type ColumnFilter,
  type ExpandedState,
  type FilterFn,
  type OnChangeFn,
  type RowPinningState,
  type SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  noop,
  useReactTable,
} from "@tanstack/react-table";
import { formatInTimeZone } from "date-fns-tz";
import Decimal from "decimal.js";
import { useQueryState } from "nuqs";
import { useCallback, useMemo, useState } from "react";
import type { ZodType, ZodTypeDef } from "zod";

import { rankItem } from "../../../lib/miscellaneous/search-ranking";
import useTableSorting from "../tables/sortingPersistence";
import useTableShortcuts from "../tables/tableShortCuts";

type UseTableProps<T, TFilterFields extends string = string> = {
  tableName: string;
  data: T[];
  columns: ColumnDef<T>[];
  clickableColumns?: string[];
  initialColumnVisibility?: Record<string, boolean>;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (value: Record<string, boolean>) => void;
  getRowId?: (row: T) => string;
  onRowActions?: Record<string, (rowId: string) => void>;
  getExpandedRowModel?: any;
  filterFromLeafRows?: boolean;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getSubRows?: (row: T) => T[];
  meta?: Record<string, any>;
  initialSorting?: SortingState;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  keepExpandedRowsOnDepthLevel?: number[];
  columnFilters?: ColumnFilter[];
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  onColumnFilterChange?: (filterState: any) => void;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  focusedRowUrlParamName?: string;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (value: Record<string, boolean>) => void;
  rowPinning?: RowPinningState;
  onRowPinningChange?: (value: RowPinningState) => void;
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  footer?: React.ReactNode | false;
  enableSubRowSelection?: boolean;
  serverSideFilters?: ServerFilters<TFilterFields>;
  initialServerSideFilters?: ServerFilters<TFilterFields>;
  globalServerSideSearch?: string;
  initialGlobalServerSideSearch?: string;
  onServerSideFiltersClear?: () => void;
};

export function useTable<T>({
  data,
  tableName,
  columns: tableColumns,
  clickableColumns = [],
  initialColumnVisibility = {},
  columnVisibility: _columnVisibility,
  onColumnVisibilityChange: _onColumnVisibilityChange,
  filterFromLeafRows,
  expanded,
  onExpandedChange,
  getExpandedRowModel,
  getSubRows,
  keepExpandedRowsOnDepthLevel,
  getRowId = (row: T) => (row as any)?.id,
  initialSorting,
  columnFilters,
  globalFilter,
  onGlobalFilterChange,
  onColumnFilterChange,
  manualFiltering = false,
  manualPagination = false,
  manualSorting = false,
  meta = {},
  focusedRowUrlParamName = "focusedRowId",
  rowSelection: _rowSelection,
  rowPinning: _rowPinning,
  onRowSelectionChange: _onRowSelectionChange,
  onRowPinningChange: _onRowPinningChange,
  sorting: _sorting,
  onSortingChange: _onSortingChange,
  stickyColumns,
  footer = true,
  enableSubRowSelection = true,
  serverSideFilters,
  globalServerSideSearch,
  initialServerSideFilters,
  initialGlobalServerSideSearch,
  onServerSideFiltersClear,
}: UseTableProps<T>) {
  const [_internalRowSelection, _setInternalRowSelection] = useState({});
  const rowSelection = _rowSelection ?? _internalRowSelection;

  const [_internalColumnVisibility, _setInternalColumnVisibility] = useState(
    initialColumnVisibility,
  );
  const columnVisibility = _columnVisibility ?? _internalColumnVisibility;

  const [focusedRowId, setFocusedRowId] = useQueryState(focusedRowUrlParamName);
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const [internalRowPinning, setInternalRowPinning] = useState<RowPinningState>(
    {
      top: [],
      bottom: [],
    },
  );
  const rowPinning = _rowPinning ?? internalRowPinning;
  const [exitingRows, setExitingRows] = useState<Record<string, boolean>>({});

  const { sorting: internalSorting, onSortingChange: internalOnSortingChange } =
    useTableSorting(tableName, initialSorting);

  const sorting = _sorting ?? internalSorting;
  const onSortingChange = _onSortingChange ?? internalOnSortingChange;

  const fuzzyFilter: FilterFn<any> = (row, columnId, searchValue, addMeta) => {
    let adaptedSearchValue = searchValue;
    let columnValue = row.getValue(columnId);

    if (!(typeof columnValue === "string" || typeof columnValue === "number")) {
      return false;
    }

    const decimalRegex = /^\d+([,.]\d{0,2})?$/;
    const trimmedSearchValue = searchValue.trim();

    if (
      typeof columnValue === "number" &&
      decimalRegex.test(trimmedSearchValue)
    ) {
      adaptedSearchValue = trimmedSearchValue.replace(/[,.]/g, "");
      columnValue = new Decimal(columnValue).mul(100).toString();
    }

    if (typeof columnValue === "string") {
      const date = new Date(columnValue);
      const columnValueIsDate =
        !Number.isNaN(date.getTime()) &&
        (columnValue.includes("-") || columnValue.includes("/"));
      if (columnValueIsDate) {
        columnValue = formatInTimeZone(date, "Europe/Berlin", "dd.MM.yy");
      }
    }

    const itemRank = rankItem(columnValue, adaptedSearchValue);

    addMeta({
      itemRank,
    });

    return itemRank.passed;
  };

  const handleRowFocusChange = useCallback(
    (
      updaterOrValue:
        | string
        | undefined
        | ((old: string | undefined) => string | undefined),
    ) => {
      if (typeof updaterOrValue === "function") {
        setFocusedRowId((old) => {
          const newValue = updaterOrValue(old ?? undefined);
          return newValue ?? null;
        });
      } else {
        setFocusedRowId(updaterOrValue ?? null);
      }
    },
    [setFocusedRowId],
  );

  const setRowSelection = useCallback(
    (updaterOrValue: any) => {
      if (_onRowSelectionChange) {
        _onRowSelectionChange(updaterOrValue);
      } else {
        _setInternalRowSelection(updaterOrValue);
      }
    },
    [_onRowSelectionChange],
  );

  const setRowPinning = useCallback(
    (updaterOrValue: any) => {
      if (_onRowPinningChange) {
        _onRowPinningChange(updaterOrValue);
      } else {
        setInternalRowPinning(updaterOrValue);
      }
    },
    [_onRowPinningChange],
  );

  const setColumnVisibility = useCallback(
    (updaterOrValue: any) => {
      if (_onColumnVisibilityChange) {
        _onColumnVisibilityChange(updaterOrValue);
      } else {
        _setInternalColumnVisibility(updaterOrValue);
      }
    },
    [_onColumnVisibilityChange],
  );

  const columns = useMemo(() => {
    return tableColumns;
  }, [tableColumns]);

  const table = useReactTable<T>({
    _features: [
      RowFocusFeature,
      TableFilterFeature,
      RowLoadingFeature,
      RowExpanding,
      RowExitingFeature,
      ServerSideFilteringFeature,
    ],
    data,
    columns,
    enableRowFocus: true,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    onExpandedChange,
    getSubRows,
    onGlobalFilterChange: onGlobalFilterChange,
    globalFilterFn: fuzzyFilter,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: onColumnFilterChange,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel?.(),
    onRowSelectionChange: setRowSelection,
    onRowFocusChange: handleRowFocusChange,
    onRowLoadingChange: setLoadingRows,
    onRowPinningChange: setRowPinning,
    onColumnVisibilityChange: setColumnVisibility,
    onRowExitingChange: setExitingRows,
    keepPinnedRows: true,
    getColumnCanGlobalFilter: () => true,
    keepExpandedRowsOnDepthLevel,
    getRowId,
    filterFromLeafRows,
    manualFiltering,
    manualPagination,
    manualSorting,
    enableSubRowSelection,
    initialServerSideFilters,
    initialGlobalServerSideSearch,
    onServerSideFiltersClear,
    state: {
      sorting,
      expanded,
      columnFilters,
      globalFilter,
      rowSelection,
      focusedRowId: focusedRowId ?? undefined,
      loadingRows,
      rowPinning,
      columnVisibility,
      exitingRows,
      serverSideFilters,
      globalServerSideSearch,
    },
    initialState: {
      columnVisibility: initialColumnVisibility,
    },
    debugTable: process.env.NODE_ENV === "development",
    defaultColumn: {
      size: undefined,
    },
  });

  useTableShortcuts({
    handleCmdA: () => table.toggleAllRowsSelected(true),
    handleEsc: () => table.resetRowSelection(true),
  });

  const setColumnFilters = useCallback(
    (_filters: ColumnFilter[]) => {
      const currentFilters = table.getState().columnFilters;

      const filters = _filters.map((filter) => ({
        id: filter.id,
        value: filter.value,
      }));

      const currentFilterMap = new Map(
        currentFilters.map((filter) => [filter.id, filter]),
      );

      filters.forEach((filter) => {
        currentFilterMap.set(filter.id, filter);
      });

      const mergedFilters = Array.from(currentFilterMap.values());

      table.setColumnFilters(mergedFilters);
    },
    [table],
  );

  return {
    data,
    table,
    columns,
    tableName,
    setRowSelection,
    setRowPinning,
    setColumnFilters,
    setColumnVisibility,
    onGlobalFilterChange: onGlobalFilterChange ?? noop,
    globalFilter: globalFilter ?? "",
    onColumnFilterChange: onColumnFilterChange ?? noop,
    columnFilters: columnFilters ?? [],
    focusedRowId: focusedRowId ?? undefined,
    loadingRows,
    clickableColumns,
    columnVisibility,
    meta,
    rowPinning,
    rowSelection,
    stickyColumns,
    footer,
  };
}
